/**
 * Third-person orbit camera.
 *
 * Three things beyond "follow the player" make a third-person camera feel good,
 * and the V1 rig had none of them (VISUAL_AUDIT V-18):
 *
 *  1. Different response rates for position and aim. The camera should chase
 *     the player loosely but keep the aim tight, or fast movement feels like
 *     dragging the world around by a rope.
 *  2. Speed response. A small FOV widening and pull-back while running is most
 *     of what makes running read as fast rather than as walking with a bigger
 *     number.
 *  3. Graceful occlusion. Snapping the camera through a wall is jarring; fading
 *     the obstruction is not always possible, so the camera pulls in smoothly
 *     and orbits for a clear line when the whole line is blocked.
 */
import * as THREE from 'three';
import type { Collider } from '../world/Neighbourhood.js';

const MIN_PITCH = -0.14;
const MAX_PITCH = 1.05;
const MIN_DISTANCE = 4.5;
const MAX_DISTANCE = 20;
/** The camera never comes closer to the avatar than this, whatever is in the way. */
const MIN_CLEARANCE = 4.0;
/** Indoors: close enough to sit inside a small room. */
const INDOOR_DISTANCE = 3.6;
const INDOOR_CLEARANCE = 1.5;

// 58 was wide enough to bend the buildings at the frame edge and to fill the
// bottom third with empty tarmac. 52 frames the avatar in the world instead.
const BASE_FOV = 52;
const RUN_FOV = 59;

export class CameraRig {
  yaw = Math.PI;
  pitch = 0.42;
  distance = 9.6;

  private readonly desired = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private readonly smoothedLook = new THREE.Vector3();
  private smoothed = new THREE.Vector3();
  private initialised = false;
  /** Eased separately from `distance` so occlusion does not fight the player. */
  private occlusionDistance = 9.6;
  private shake = 0;
  /**
   * While working, the camera pulls back and up to frame the whole property.
   * Mowing at walking distance means staring at a wall for ninety minutes.
   */
  private workFraming = 0;
  private workAnchor: THREE.Vector3 | null = null;
  /** 0..1, eased. Driving pulls the camera back and flattens the pitch. */
  private drivingFraming = 0;
  private wantDriving = 0;
  /**
   * Indoors the whole framing has to shrink.
   *
   * MIN_CLEARANCE is 4 m, which in a 6.2 m room is already outside the far
   * wall - and the occlusion test deliberately ignores anything the camera is
   * above, so it happily flew over the wall and framed the room from outside
   * like a doll's house. Both of those are correct outdoors and wrong here.
   */
  private indoors = false;
  private indoorDistance = INDOOR_DISTANCE;

  constructor(readonly camera: THREE.PerspectiveCamera) {
    camera.fov = BASE_FOV;
    camera.updateProjectionMatrix();
  }

  /** Shrink the framing for a room, and stop the camera leaving through walls. */
  setIndoors(indoors: boolean, maxDistance = INDOOR_DISTANCE): void {
    this.indoors = indoors;
    this.indoorDistance = maxDistance;
  }

  /** Reframe for driving. Eased inside `update`, not applied immediately. */
  setDriving(driving: boolean): void {
    this.wantDriving = driving ? 1 : 0;
  }

  look(dx: number, dy: number, sensitivity = 0.0024): void {
    this.yaw -= dx * sensitivity;
    this.pitch = clamp(this.pitch + dy * sensitivity, MIN_PITCH, MAX_PITCH);
  }

  zoom(delta: number): void {
    this.distance = clamp(this.distance + delta * 0.009, MIN_DISTANCE, MAX_DISTANCE);
  }

  /** A short kick, for impacts and completions. */
  addShake(amount: number): void {
    this.shake = Math.min(1, this.shake + amount);
  }

  /**
   * Frame a whole property rather than the avatar. Passing null returns to
   * normal third-person framing.
   */
  setWorkFocus(centre: { x: number; z: number } | null): void {
    this.workAnchor = centre ? new THREE.Vector3(centre.x, 0, centre.z) : null;
  }

  /**
   * @param speedFraction 0..1 against run speed, drives FOV and pull-back
   */
  update(
    dt: number,
    target: THREE.Vector3,
    colliders: Collider[],
    speedFraction = 0,
  ): void {
    // Ease into and out of the wider working framing.
    const wantWork = this.workAnchor ? 1 : 0;
    this.workFraming += (wantWork - this.workFraming) * Math.min(1, dt * 1.6);
    // Eased over about a second, so getting in and out of the car is a move
    // rather than a cut. A hard switch here reads as a camera bug.
    this.drivingFraming += (this.wantDriving - this.drivingFraming) * Math.min(1, dt * 2.4);

    // Aim slightly above the avatar's centre; looking at the feet feels wrong.
    this.lookTarget.set(target.x, target.y + 1.45, target.z);
    // Working raises the aim a little and pulls the camera back; it deliberately
    // does NOT drag the aim point toward the middle of the property, because
    // that point is inside the house.
    if (this.workFraming > 0.01) this.lookTarget.y += this.workFraming * 1.4;
    if (!this.initialised) this.smoothedLook.copy(this.lookTarget);
    // The aim follows faster than the body, which is what stops the frame
    // feeling like it is being towed.
    this.smoothedLook.lerp(this.lookTarget, Math.min(1, 16 * dt));

    // Running pulls the camera back a little and widens the lens; working pulls
    // back much further so the whole yard is in shot.
    let wantedDistance =
      this.distance
      * (1 + speedFraction * 0.14)
      * (1 + this.workFraming * 0.85)
      * (1 + this.drivingFraming * 0.55);
    // Per room: a 7 m bedsit and a 14 m shop floor want very different
    // framing, and one number for both makes the shop a close-up of the
    // player's back.
    if (this.indoors) wantedDistance = Math.min(wantedDistance, this.indoorDistance);
    const clear = this.clearDistance(wantedDistance, colliders);
    // Pull in fast when something intrudes, ease back out slowly — the reverse
    // reads as the camera being shoved.
    const easeRate = clear < this.occlusionDistance ? 26 : 4.5;
    this.occlusionDistance += (clear - this.occlusionDistance) * Math.min(1, easeRate * dt);

    // Driving lowers the pitch: at speed the useful information is the road
    // ahead, and a high angle turns it into a map.
    let workPitch = this.pitch + this.workFraming * 0.24 - this.drivingFraming * 0.16;
    // A steep angle in a room puts the camera in the ceiling.
    if (this.indoors) workPitch = Math.min(workPitch, 0.34);
    const horizontal = Math.cos(workPitch) * this.occlusionDistance;
    this.desired.set(
      this.smoothedLook.x + Math.sin(this.yaw) * horizontal,
      this.smoothedLook.y + Math.sin(workPitch) * this.occlusionDistance,
      this.smoothedLook.z + Math.cos(this.yaw) * horizontal,
    );
    this.desired.y = Math.max(this.desired.y, 1.1);

    if (!this.initialised) {
      this.smoothed.copy(this.desired);
      this.initialised = true;
    } else {
      this.smoothed.lerp(this.desired, Math.min(1, 10 * dt));
    }

    this.camera.position.copy(this.smoothed);

    if (this.shake > 0.001) {
      this.shake = Math.max(0, this.shake - dt * 2.6);
      const magnitude = this.shake * this.shake * 0.22;
      this.camera.position.x += (Math.random() - 0.5) * magnitude;
      this.camera.position.y += (Math.random() - 0.5) * magnitude;
    }

    this.camera.lookAt(this.smoothedLook);

    const wantedFov = BASE_FOV + (RUN_FOV - BASE_FOV) * speedFraction;
    if (Math.abs(this.camera.fov - wantedFov) > 0.01) {
      this.camera.fov += (wantedFov - this.camera.fov) * Math.min(1, 5 * dt);
      this.camera.updateProjectionMatrix();
    }
  }

  /** Snap immediately, used on load and teleport so the camera does not sweep. */
  snap(): void {
    this.initialised = false;
    this.occlusionDistance = this.distance;
  }

  /**
   * Point the camera so the avatar is seen from the open side.
   *
   * Fast travel drops the player at a building's doorway with the building
   * directly behind them, so without this every arrival would start with the
   * camera fighting a wall.
   */
  faceFrom(outwardX: number, outwardZ: number): void {
    if (outwardX === 0 && outwardZ === 0) return;
    this.yaw = Math.atan2(outwardX, outwardZ);
    this.snap();
  }

  /**
   * How far back the camera can sit before something is in the way.
   *
   * Walks outward from the avatar along the view direction and stops at the
   * first blocked sample. If even the closest sample is blocked — standing in a
   * doorway with the building behind you — orbits sideways for a clear line,
   * because pulling straight in leaves the camera inside a wall looking at
   * unlit back faces and swinging overhead loses the horizon.
   */
  private clearDistance(wanted: number, colliders: Collider[]): number {
    // The test is 2D on the ground plane plus a height compare: a camera above
    // a rooftop is not blocked by the building beneath it, which is what makes
    // the raised working framing possible at all.
    // Outdoors the height compare is what lets the camera rise above a
    // rooftop rather than be blocked by the building beneath it. Indoors
    // there is a ceiling, so every wall blocks at any height - without this
    // the camera clears a 2.65 m wall at the distance it wants and frames the
    // room from outside like a doll's house.
    const blocked = (x: number, z: number, y: number) =>
      colliders.some(
        (c) =>
          (this.indoors || y < c.top) &&
          x > c.minX - 0.45 && x < c.maxX + 0.45 &&
          z > c.minZ - 0.45 && z < c.maxZ + 0.45,
      );

    const pitch = this.pitch + this.workFraming * 0.24;
    const sample = (d: number, yaw: number) => {
      const horizontal = Math.cos(pitch) * d;
      return {
        x: this.smoothedLook.x + Math.sin(yaw) * horizontal,
        z: this.smoothedLook.z + Math.cos(yaw) * horizontal,
        y: this.smoothedLook.y + Math.sin(pitch) * d,
      };
    };

    const steps = 12;
    const floor = this.indoors ? INDOOR_CLEARANCE : MIN_CLEARANCE;
    let lastClear = floor;
    for (let i = 1; i <= steps; i++) {
      const d = floor + (wanted - floor) * (i / steps);
      const p = sample(d, this.yaw);
      if (blocked(p.x, p.z, p.y)) break;
      lastClear = d;
    }
    if (lastClear > floor + 0.01) return lastClear;

    // The whole line is blocked. Orbit for a clear direction rather than
    // burying the camera in the wall.
    for (let degrees = 20; degrees <= 180; degrees += 20) {
      for (const sign of [1, -1]) {
        const yaw = this.yaw + (degrees * Math.PI / 180) * sign;
        const p = sample(floor, yaw);
        if (!blocked(p.x, p.z, p.y)) {
          // Nudge the player's own yaw toward the clear side so the camera does
          // not sit fighting the wall for as long as they stand there.
          this.yaw += (degrees * Math.PI / 180) * sign * 0.35;
          return floor;
        }
        if (degrees === 180) break;
      }
    }
    return floor;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
